import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  Globe,
  PackageCheck,
  ShoppingCart,
  Store,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { usdCompact } from '@/lib/format';
import { normaliseGroupKey, resolveProductHref, type GroupKind } from '@/lib/url';
import type {
  RenovationItemDto,
  RenovationItemStatus,
  RenovationRoomDto,
} from '@cc/types';

interface Props {
  projectId: string;
  items: RenovationItemDto[];
  rooms: RenovationRoomDto[];
}

interface Group {
  key: string;
  label: string;
  kind: GroupKind;
  items: RenovationItemDto[];
}

/**
 * Consolidates renovation items by supplier (host of productUrl, or vendor name
 * for items without a URL) so Karl can work through one shopping cart at a
 * time. Tick items into-cart as he adds them on the supplier's website, then
 * bulk-mark them ordered when checkout completes.
 */
export function ConsolidationView({ projectId, items, rooms }: Props) {
  const queryClient = useQueryClient();
  const [showOrdered, setShowOrdered] = useState(false);
  const roomNameById = useMemo(() => new Map(rooms.map((r) => [r.id, r.name])), [rooms]);

  const updateItem = useMutation({
    mutationFn: (args: {
      itemId: string;
      patch: Partial<RenovationItemDto> & Record<string, unknown>;
    }) => api.updateItem(projectId, args.itemId, args.patch),
    onError: () => toast.error('Failed to update item.'),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.renovation(projectId) });
    },
  });

  const groups = useMemo(
    () => buildGroups(items, showOrdered),
    [items, showOrdered],
  );

  const toggleInCart = (item: RenovationItemDto) => {
    updateItem.mutate({
      itemId: item.id,
      patch: { inCart: !item.inCart },
    });
  };

  const markGroupOrdered = async (group: Group) => {
    const targets = group.items.filter((it) => it.inCart && !it.materialsOrdered);
    if (targets.length === 0) {
      toast.message('Nothing in-cart in this group.');
      return;
    }
    try {
      await Promise.all(
        targets.map((it) =>
          api.updateItem(projectId, it.id, {
            materialsOrdered: true,
            inCart: false,
            // Auto-advance status only when it makes sense; never regress
            // already-progressed work.
            ...(it.status === 'NOT_STARTED' || it.status === 'PLANNING'
              ? { status: 'ORDERED' as RenovationItemStatus }
              : {}),
          }),
        ),
      );
      toast.success(`Marked ${targets.length} item(s) ordered from ${group.label}.`);
    } catch {
      toast.error('Failed to mark all ordered. Some items may have updated.');
    } finally {
      void queryClient.invalidateQueries({ queryKey: qk.renovation(projectId) });
    }
  };

  const openAllInGroup = (group: Group) => {
    const urls = group.items
      .filter((it) => it.productUrl && !it.materialsOrdered)
      .map((it) => resolveProductHref(it.productUrl!));
    if (urls.length === 0) {
      toast.message('No product links in this group.');
      return;
    }
    // Best-effort; browsers may block all but the first popup. The user has to
    // interact for each set, but it's still faster than copy-pasting.
    let blocked = 0;
    for (const u of urls) {
      const w = window.open(u, '_blank', 'noopener,noreferrer');
      if (!w) blocked += 1;
    }
    if (blocked > 0) {
      toast.warning(
        `Browser blocked ${blocked} popup(s). Allow popups for this site to open all at once.`,
      );
    }
  };

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm text-muted-foreground">
          Add items in the Rooms tab first.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Grouped by supplier (URL host, falling back to vendor name). Tick items as
          you add them to the supplier's cart, then mark the group ordered when
          you check out.
        </p>
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={showOrdered}
            onChange={(e) => setShowOrdered(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Show ordered
        </label>
      </div>

      {groups.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            {showOrdered
              ? 'No items match this view.'
              : 'No outstanding orders — everything is already marked as ordered. Toggle "Show ordered" to see the full list.'}
          </CardContent>
        </Card>
      ) : (
        groups.map((g) => (
          <SupplierCard
            key={g.key}
            group={g}
            roomNameById={roomNameById}
            onToggleInCart={toggleInCart}
            onMarkOrdered={() => void markGroupOrdered(g)}
            onOpenAll={() => openAllInGroup(g)}
            busy={updateItem.isPending}
          />
        ))
      )}
    </div>
  );
}

function SupplierCard({
  group,
  roomNameById,
  onToggleInCart,
  onMarkOrdered,
  onOpenAll,
  busy,
}: {
  group: Group;
  roomNameById: Map<string, string>;
  onToggleInCart: (item: RenovationItemDto) => void;
  onMarkOrdered: () => void;
  onOpenAll: () => void;
  busy: boolean;
}) {
  const totalEstimated = group.items.reduce(
    (acc, it) => acc + (it.estimatedCost ?? 0),
    0,
  );
  const inCartTotal = group.items.reduce(
    (acc, it) => acc + (it.inCart ? it.estimatedCost ?? 0 : 0),
    0,
  );
  const inCartCount = group.items.filter((it) => it.inCart).length;
  const KindIcon = group.kind === 'host' ? Globe : group.kind === 'vendor' ? Store : PackageCheck;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <KindIcon className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">{group.label}</h3>
            <Badge variant="muted" className="text-[10px]">
              {group.items.length} item{group.items.length === 1 ? '' : 's'}
            </Badge>
            <span className="text-[11px] text-muted-foreground">
              {usdCompact(totalEstimated)} total
            </span>
            {inCartCount > 0 && (
              <Badge variant="success" className="text-[10px]">
                {inCartCount} in cart · {usdCompact(inCartTotal)}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {group.kind === 'host' && (
              <Button
                size="sm"
                variant="outline"
                onClick={onOpenAll}
                disabled={busy}
                title="Open every product link in a new tab"
                className="h-7 px-2 text-xs"
              >
                <ExternalLink className="mr-1 h-3 w-3" /> Open all
              </Button>
            )}
            <Button
              size="sm"
              onClick={onMarkOrdered}
              disabled={busy || inCartCount === 0}
              title={
                inCartCount === 0
                  ? 'Tick at least one item as in-cart first'
                  : 'Mark all in-cart items as ordered'
              }
              className="h-7 px-2 text-xs"
            >
              <ShoppingCart className="mr-1 h-3 w-3" /> Mark {inCartCount || ''} ordered
            </Button>
          </div>
        </div>

        <ul className="space-y-1">
          {group.items.map((it) => (
            <ItemRow
              key={it.id}
              item={it}
              roomName={roomNameById.get(it.roomId) ?? '—'}
              onToggleInCart={() => onToggleInCart(it)}
              busy={busy}
            />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function ItemRow({
  item,
  roomName,
  onToggleInCart,
  busy,
}: {
  item: RenovationItemDto;
  roomName: string;
  onToggleInCart: () => void;
  busy: boolean;
}) {
  const isOrdered = item.materialsOrdered;
  return (
    <li
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-md border border-border px-2 py-1.5 text-sm transition-opacity',
        isOrdered && 'opacity-50',
        item.inCart && !isOrdered && 'border-emerald-500/40 bg-emerald-500/5',
      )}
    >
      <input
        type="checkbox"
        checked={item.inCart}
        onChange={onToggleInCart}
        disabled={busy || isOrdered}
        className="h-4 w-4 flex-none cursor-pointer"
        title={isOrdered ? 'Already ordered' : 'Toggle in-cart'}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate">{item.activity}</p>
        <p className="text-[11px] text-muted-foreground">
          {roomName}
          {isOrdered && <span className="ml-1.5 text-emerald-400">· ordered</span>}
        </p>
      </div>
      <span className="flex-none font-mono text-xs tabular-nums text-muted-foreground">
        {item.estimatedCost === null ? '—' : usdCompact(item.estimatedCost)}
      </span>
      {item.productUrl && (
        <a
          href={resolveProductHref(item.productUrl)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-none text-muted-foreground hover:text-foreground"
          title="Open product page"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </li>
  );
}

function buildGroups(items: RenovationItemDto[], showOrdered: boolean): Group[] {
  const filtered = items.filter((it) => showOrdered || !it.materialsOrdered);
  const byKey = new Map<string, Group>();
  for (const it of filtered) {
    const { key, label, kind } = normaliseGroupKey(it);
    let g = byKey.get(key);
    if (!g) {
      g = { key, label, kind, items: [] };
      byKey.set(key, g);
    }
    g.items.push(it);
  }
  return Array.from(byKey.values()).sort((a, b) => {
    // Surface the supplier with the biggest outstanding order first; push the
    // 'Uncategorised' bucket to the bottom regardless of total.
    if (a.kind === 'none' && b.kind !== 'none') return 1;
    if (b.kind === 'none' && a.kind !== 'none') return -1;
    const aTotal = a.items.reduce(
      (acc, it) => acc + (it.materialsOrdered ? 0 : it.estimatedCost ?? 0),
      0,
    );
    const bTotal = b.items.reduce(
      (acc, it) => acc + (it.materialsOrdered ? 0 : it.estimatedCost ?? 0),
      0,
    );
    if (bTotal !== aTotal) return bTotal - aTotal;
    return a.label.localeCompare(b.label);
  });
}
